import { Vec2 } from '../math/Vec2';

export interface Particle {
  pos: Vec2;
  vel: Vec2;
  life: number;
  color: string;
  size: number;
}

export interface Ripple {
  pos: Vec2;
  radius: number;
  maxRadius: number;
  color: string;
  alpha: number;
  width: number;
}

export interface FloatingText {
  pos: Vec2;
  text: string;
  color: string;
  size: number;
  life: number;
  maxLife: number;
}

export class ParticleSystem {
  particles: Particle[] = [];
  ripples: Ripple[] = [];
  floatingTexts: FloatingText[] = [];
  shakeAmount: number = 0;

  scoreRollFramesLeft: number = 0;
  scoreRollStreak: number = 0;
  scoreRollBump: number = 0;
  private lastScoreEventTime: number = -999999;
  private activeTimeouts: number[] = [];

  spawnParticles(pos: Vec2, color: string, amount: number = 15): void {
    for (let p = 0; p < amount; p++) {
      this.particles.push({
        pos: pos.clone(),
        vel: new Vec2((Math.random() - 0.5) * 12, (Math.random() - 0.5) * 12),
        life: 1.0,
        color,
        size: Math.random() * 5 + 2,
      });
    }
  }

  spawnRipples(pos: Vec2, color: string, isSmash: boolean = false): void {
    this.ripples.push({
      pos: pos.clone(),
      radius: 10,
      maxRadius: isSmash ? 150 : 60,
      color,
      alpha: 1.0,
      width: isSmash ? 8 : 4,
    });

    if (isSmash) {
      const tId = window.setTimeout(() => {
        this.ripples.push({
          pos: pos.clone(),
          radius: 10,
          maxRadius: 100,
          color: '#ffffff',
          alpha: 1.0,
          width: 4,
        });
        const idx = this.activeTimeouts.indexOf(tId);
        if (idx >= 0) this.activeTimeouts.splice(idx, 1);
      }, 100);
      this.activeTimeouts.push(tId);
    }
  }

  spawnFloatingText(
    pos: Vec2,
    text: string,
    color: string,
    size: number = 24,
    life: number = 80
  ): void {
    this.floatingTexts.push({
      pos: pos.clone(),
      text,
      color,
      size,
      life,
      maxLife: life,
    });
  }

  addScreenShake(amount: number): void {
    this.shakeAmount = Math.max(this.shakeAmount, amount);
  }

  registerScoreRoll(delta: number, currentFrame: number): void {
    if (!Number.isFinite(delta) || delta <= 0) return;
    const withinWindow = currentFrame - this.lastScoreEventTime <= 120;
    this.scoreRollStreak = withinWindow ? this.scoreRollStreak + 1 : 1;
    this.lastScoreEventTime = currentFrame;
    this.scoreRollFramesLeft = 180;
    this.scoreRollBump = Math.min(1.6, delta / 500);
  }

  update(): void {
    if (this.shakeAmount > 0.1) {
      this.shakeAmount *= 0.88;
    } else {
      this.shakeAmount = 0;
    }

    if (this.scoreRollFramesLeft > 0) this.scoreRollFramesLeft--;

    // Update particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.pos = p.pos.add(p.vel);
      p.life -= 0.03;
      if (p.life <= 0) {
        this.particles.splice(i, 1);
      }
    }

    // Update ripples
    for (let i = this.ripples.length - 1; i >= 0; i--) {
      const r = this.ripples[i];
      r.radius += 4;
      r.alpha -= 0.03;
      if (r.alpha <= 0) {
        this.ripples.splice(i, 1);
      }
    }

    // Update floating text
    for (let i = this.floatingTexts.length - 1; i >= 0; i--) {
      const ft = this.floatingTexts[i];
      ft.pos.y -= 1.5;
      ft.life--;
      if (ft.life <= 0) {
        this.floatingTexts.splice(i, 1);
      }
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    // 1. Ripples
    for (const r of this.ripples) {
      ctx.beginPath();
      ctx.arc(r.pos.x, r.pos.y, r.radius, 0, Math.PI * 2);
      ctx.strokeStyle = r.color;
      ctx.lineWidth = r.width;
      ctx.globalAlpha = Math.max(0, r.alpha);
      ctx.stroke();
    }
    ctx.globalAlpha = 1.0;

    // 2. Particles
    for (const p of this.particles) {
      ctx.globalAlpha = Math.max(0, p.life);
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.pos.x, p.pos.y, p.size * p.life, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1.0;

    // 3. Floating Texts
    for (const ft of this.floatingTexts) {
      ctx.save();
      ctx.globalAlpha = Math.max(0, ft.life / ft.maxLife);
      ctx.fillStyle = ft.color;
      ctx.font = `bold ${ft.size}px 'Rajdhani', 'Courier New', monospace`;
      ctx.textAlign = 'center';
      ctx.shadowBlur = 10;
      ctx.shadowColor = ft.color;
      ctx.fillText(ft.text, ft.pos.x, ft.pos.y);
      ctx.restore();
    }
  }

  clear(): void {
    for (const tId of this.activeTimeouts) {
      clearTimeout(tId);
    }
    this.activeTimeouts = [];
    this.particles = [];
    this.ripples = [];
    this.floatingTexts = [];
    this.shakeAmount = 0;
  }
}
